// NOTE: $axios comes from `@/store/apis` (the canonical Axios instance set
// on Vue.prototype.$axios at boot from main.js), NOT from `@/plugins/Axios`.
// `@/plugins/Axios` is dead/legacy code: it reads from a `User/` Vuex
// namespace that doesn't exist, has no 401 response interceptor, and would
// overwrite Vue.prototype.$axios with a less-featured instance — which would
// silently break the session-expired modal flow for every component.
// Required lazily inside each action so the require at module-load time
// doesn't pull `@/store/apis` into a circular dependency with the store.
import can from '@/Helpers/Can';

// Poll interval for in-flight jobs (ms)
const POLL_MS = 3000;

// Which endpoint to POST to per type
const CREATE_ENDPOINT = {
  billing_customer: 'v2/report-billing-for-customer-export',
  ar_as_of_date: 'v2/report-ar-as-of-date',
  ar_as_of_date_filtered: 'v2/report-ar-as-of-date-filtered',
  billing_payment_summary: 'v2/report-billing-totals-summary-export',
};

export default {
  namespaced: true,
  state: () => ({
    // { [jobId]: { id, type, status, row_count, file_size, download_url, error_message, ... } }
    jobs: {},
  }),
  mutations: {
    upsert(state, job) {
      // Vue 2 reactivity for dynamic keys
      this._vm.$set(state.jobs, job.id, { ...state.jobs[job.id], ...job });
    },
    remove(state, jobId) {
      this._vm.$delete(state.jobs, jobId);
    },
  },
  actions: {
    async create({ dispatch, commit }, { type, params }) {
      const { $axios } = require('@/store/apis');
      const endpoint = CREATE_ENDPOINT[type];
      if (!endpoint) throw new Error(`Unknown export type: ${type}`);
      const res = await $axios.post(endpoint, params);
      const job = res.data.data;
      commit('upsert', job);
      dispatch('poll', job.id);
      return job;
    },
    async poll({ commit, dispatch, state }, jobId) {
      if (!state.jobs[jobId]) return;
      const { $axios } = require('@/store/apis');
      try {
        // Failures already land on the job card, so skip the global 403 toast.
        const res = await $axios.get(`v2/export-jobs/${jobId}`, { silentForbidden: true });
        const job = res.data.data;
        commit('upsert', job);
        if (job.status === 'queued' || job.status === 'running') {
          setTimeout(() => dispatch('poll', jobId), POLL_MS);
        }
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || 'Network error';
        commit('upsert', { id: jobId, status: 'failed', error_message: msg });
      }
    },
    async rehydrate({ commit, dispatch }) {
      // export-jobs needs the `report` permission. Staff without it would
      // otherwise get a 403 on every page load.
      if (!can('report')) return;
      const { $axios } = require('@/store/apis');
      try {
        const res = await $axios.get('v2/export-jobs', {
          params: { status: 'queued,running,ready', limit: 5 },
          silentForbidden: true,
        });
        for (const job of res.data.data) {
          commit('upsert', job);
          if (job.status === 'queued' || job.status === 'running') {
            dispatch('poll', job.id);
          }
        }
      } catch (_) {
        /* silent — rehydrate is best-effort */
      }
    },
    dismiss({ commit }, jobId) {
      commit('remove', jobId);
    },
  },
  getters: {
    activeJobs: (s) => Object.values(s.jobs),
  },
};
